// Renders the per-field input(s) for a single devotional entry.
// Inputs adapt to the field's `field_type`:
//   - text                  → <Input>
//   - textarea              → <Textarea>
//   - rich_text / editor    → <Textarea> (multi-line, hint that HTML is supported)
//   - scripture_reference   → <Input> (single reference like "John 3:16")
//   - list                  → repeating-row editor producing a string[] value
//
// Each field also offers an optional per-entry `audio_url` slot (if the field's
// allow_audio is enabled at type level) so the mobile player can attach
// pre-recorded narration to that specific entry.

import { Plus, X, Mic } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RichTextEditor } from "@/components/RichTextEditor";

const FIELD_TYPE_LABEL = {
  text: "Short text",
  textarea: "Long text",
  rich_text: "Rich text (HTML)",
  editor: "Formatted",
  scripture_reference: "Scripture reference",
  list: "List",
};

function ListEditor({ value, onChange, placeholder }) {
  const items = Array.isArray(value) ? value : [];
  const set = (idx, next) =>
    onChange(items.map((it, i) => (i === idx ? next : it)));
  const add = () => onChange([...items, ""]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      {items.length === 0 && (
        <p className="text-xs text-muted-foreground italic">No items yet. Click "Add item" to start.</p>
      )}
      {items.map((it, idx) => (
        <div key={idx} className="flex gap-2 items-start">
          <span className="text-xs text-muted-foreground pt-2.5 select-none">{idx + 1}.</span>
          <Textarea
            data-testid={`list-item-${idx}`}
            rows={2}
            value={it}
            placeholder={placeholder}
            onChange={(e) => set(idx, e.target.value)}
            className="flex-1"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => remove(idx)}
            className="text-destructive hover:bg-destructive/10 mt-1"
            aria-label="Remove item"
          >
            <X className="size-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={add}
        data-testid="list-add-item-btn"
      >
        <Plus className="size-3.5 mr-1" /> Add item
      </Button>
    </div>
  );
}

export function EntryField({ field, value, audioUrl, onChange, onAudioChange }) {
  const t = field.field_type;
  const inputId = `entry-${field.field_key}`;
  const placeholder = `Enter ${field.field_name.toLowerCase()}…`;

  const handleValueChange = (v) => onChange(field.field_key, v);

  return (
    <div className="space-y-1.5" data-testid={`entry-field-${field.field_key}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          <Label htmlFor={inputId} className="text-sm font-medium">
            {field.field_name}
            {field.required && <span className="text-destructive ml-0.5">*</span>}
          </Label>
          <Badge variant="secondary" className="text-[10px] font-medium">
            {FIELD_TYPE_LABEL[t] || t}
          </Badge>
        </div>
        <span className="text-[10px] text-muted-foreground font-mono">{field.field_key}</span>
      </div>

      {/* Main value editor */}
      {t === "text" && (
        <Input
          id={inputId}
          data-testid={`entry-input-${field.field_key}`}
          value={value ?? ""}
          placeholder={placeholder}
          onChange={(e) => handleValueChange(e.target.value)}
        />
      )}

      {t === "scripture_reference" && (
        <Input
          id={inputId}
          data-testid={`entry-input-${field.field_key}`}
          value={value ?? ""}
          placeholder="e.g. John 3:16"
          onChange={(e) => handleValueChange(e.target.value)}
        />
      )}

      {t === "textarea" && (
        <Textarea
          id={inputId}
          data-testid={`entry-input-${field.field_key}`}
          rows={4}
          value={value ?? ""}
          placeholder={placeholder}
          onChange={(e) => handleValueChange(e.target.value)}
        />
      )}

      {t === "rich_text" && (
        <>
          <Textarea
            id={inputId}
            data-testid={`entry-input-${field.field_key}`}
            rows={8}
            value={value ?? ""}
            placeholder={`<p>${placeholder}</p>`}
            onChange={(e) => handleValueChange(e.target.value)}
            className="font-mono text-sm"
          />
          <p className="text-[10px] text-muted-foreground">
            Raw HTML. Use &lt;p&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;ul&gt;, &lt;ol&gt;, &lt;li&gt;, &lt;br&gt;.
          </p>
        </>
      )}

      {t === "editor" && (
        <RichTextEditor
          value={value ?? ""}
          onChange={handleValueChange}
          placeholder={placeholder}
          testId={`entry-input-${field.field_key}`}
        />
      )}

      {(t === "list" || t === "numbered_list") && (
        <ListEditor
          value={value}
          onChange={handleValueChange}
          placeholder={`Enter an item…`}
          numbered={t === "numbered_list"}
        />
      )}

      {/* Per-entry audio URL — only when the type's field allows audio */}
      {field.allow_audio && (
        <div className="pt-1 flex items-center gap-1.5">
          <Mic className="size-3.5 text-muted-foreground shrink-0" />
          <Input
            id={`${inputId}-audio`}
            data-testid={`entry-audio-${field.field_key}`}
            type="url"
            value={audioUrl ?? ""}
            placeholder="Optional audio narration URL (.mp3)"
            onChange={(e) => onAudioChange(field.field_key, e.target.value)}
            className="text-xs h-8"
          />
        </div>
      )}
    </div>
  );
}

/**
 * Renders all entry inputs for a devotional, given the type's `fields` array.
 * Maintains entries in `{ field_key → string|string[] }` shape and per-field
 * audio URLs in `{ field_key → url }` shape.
 *
 * Props:
 *   fields:    DevotionalField[]   — from /api/devotional-fields?type_id=…
 *   values:    Record<string, any> — current entries
 *   audioUrls: Record<string, string> — current audio URLs
 *   onChange(entries, audioUrls)    — fires on every keystroke
 */
export function EntriesForm({ fields, values, audioUrls, onChange }) {
  if (!fields || fields.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-4 text-center text-xs text-muted-foreground">
        This devotional type has no fields configured yet. Go to{" "}
        <strong>Devotional Types → Edit</strong> to add fields (or apply a template), then come back
        here.
      </div>
    );
  }

  const setValue = (key, v) => onChange({ ...values, [key]: v }, audioUrls);
  const setAudio = (key, v) => onChange(values, { ...audioUrls, [key]: v });

  return (
    <div className="space-y-5">
      {fields.map((f) => (
        <EntryField
          key={f.id}
          field={f}
          value={values[f.field_key]}
          audioUrl={audioUrls[f.field_key]}
          onChange={setValue}
          onAudioChange={setAudio}
        />
      ))}
    </div>
  );
}
